import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { ClickAwayListener } from '@mui/material';

import { HabitIconCatalogContext } from '../../context';
import { getIconComponent } from '../../habitIcons';
import {
  StyledPopper,
  SearchWrapper,
  SearchInput,
  IconGrid,
  IconCell,
} from './styles';

const HabitIconPicker = ({
  anchorEl,
  selectedIconId,
  usedIconIds,
  onChange,
  onClose,
}) => {
  const { icons } = useContext(HabitIconCatalogContext);
  const [search, setSearch] = useState('');
  const usedIconIdSet = useMemo(() => new Set(usedIconIds), [usedIconIds]);

  const filteredIcons = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return icons;
    }
    return icons.filter((id) => id.includes(query));
  }, [icons, search]);

  const handleSelect = useCallback((iconId) => {
    onChange(iconId);
    onClose();
  }, [onChange, onClose]);

  return (
    <StyledPopper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: 'viewport',
            padding: 8,
          },
        },
      ]}
    >
      <ClickAwayListener onClickAway={onClose}>
        <div>
          <SearchWrapper>
            <SearchInput
              size="small"
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="outlined"
            />
          </SearchWrapper>
          <IconGrid>
            {filteredIcons.map((iconId) => {
              const Icon = getIconComponent(iconId);
              const isSelected = iconId === selectedIconId;
              const isDisabled = usedIconIdSet.has(iconId);
              return (
                <IconCell
                  key={iconId}
                  $selected={isSelected}
                  $disabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) {
                      handleSelect(iconId);
                    }
                  }}
                  title={iconId}
                >
                  <Icon size={20} />
                </IconCell>
              );
            })}
          </IconGrid>
        </div>
      </ClickAwayListener>
    </StyledPopper>
  );
};

HabitIconPicker.propTypes = {
  anchorEl: PropTypes.object,
  selectedIconId: PropTypes.string,
  usedIconIds: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

HabitIconPicker.defaultProps = {
  anchorEl: null,
  selectedIconId: null,
  usedIconIds: [],
};

export default HabitIconPicker;
